import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '@/api/client'
import { offlineCaptureAvailable, type OfflineCapability } from './capability'
import {
  appendLinesToPendingOfflineOrder,
  enqueueOfflineOrder,
  listOfflineOrders,
  type OfflineLine,
  type OfflineOrder,
} from './offlineStore'
import { poolSize, refillPool, takeReservedNumber } from './orderNumberPool'
import { useOnlineAndReachable } from './reachability'
import { replayOfflineQueue, type ReplaySummary } from './replay'
import { desktop } from '@/lib/printTransport'

/**
 * Everything the counter needs to take a sale while the internet is down.
 *
 * Kept out of CounterInterface.tsx deliberately — that file is already huge
 * (F-FE-N4), and offline capture is a self-contained concern with its own
 * lifecycle: keep a block of order numbers warm while online, decide honestly
 * whether capture is possible, and drain the queue on reconnect.
 */

export interface CaptureInput {
  /**
   * Set when appending to an order that is STILL QUEUED on this device
   * (F-FE-N31) — it has no server id yet, so the append is a local mutation of
   * the queued entry and the whole thing replays later as ONE create.
   *
   * Takes precedence over targetOrderId: an order cannot be both.
   */
  heldOpId?: string | null
  /**
   * Set when appending to an order that ALREADY exists on the server
   * (F-CTRL-N37). Null for a brand-new order. Drives whether replay creates an
   * order or appends to one.
   */
  targetOrderId?: string | null
  /** Order number of the order being appended to. */
  existingOrderNumber?: string
  orderType: string
  orderNote: string
  userId: string
  lines: OfflineLine[]
}

/**
 * Why this is a discriminated union rather than `CaptureResult | null`: the two
 * ways a capture can fail need completely different handling. "Not ready" means
 * nothing was taken and the cashier must be told this terminal cannot serve
 * offline. "Already synced" means the held order posted while they were
 * composing — the items are still good, they just belong on the real order now.
 * Collapsing both to null produced one vague toast and risked dropping a round.
 */
export type CaptureResult =
  | {
      ok: true
      orderNumber: string
      /** The updated queue entry, for an append to a held order. Null otherwise. */
      held: OfflineOrder | null
    }
  | { ok: false; reason: 'not_ready' | 'already_synced' }

export function useOfflineCapture(opts: { online: boolean; todayISO: string }) {
  const { online: osOnline, todayISO } = opts
  // F-FE-N30: subscribe so a reachability flip re-evaluates the predicate
  // immediately. Reading the value once would leave the button stale until some
  // other state change happened to re-render.
  const { backendReachable } = useOnlineAndReachable()
  // Treat the terminal as connected only when BOTH agree.
  const online = osOnline && backendReachable
  const [capability, setCapability] = useState<OfflineCapability>({ ok: false, reason: 'online' })
  const [pendingCount, setPendingCount] = useState(0)
  const [unreconciledCount, setUnreconciledCount] = useState(0)

  const refreshCounts = useCallback(async () => {
    const all = await listOfflineOrders()
    setPendingCount(all.filter((o) => o.status === 'pending').length)
    setUnreconciledCount(all.filter((o) => o.status === 'unreconciled').length)
  }, [])

  /** Recompute the capability predicate from what this terminal actually holds. */
  const refreshCapability = useCallback(async () => {
    setCapability(
      offlineCaptureAvailable({
        online: osOnline,
        // F-FE-N30: the load-bearing signal. `osOnline` alone stays true when
        // the router is up and the ISP is down — the outage this exists for.
        backendReachable,
        isDesktop: desktop() != null,
        reservedNumbers: await poolSize(todayISO),
      }),
    )
  }, [osOnline, backendReachable, todayISO])

  // While online, keep the number block warm. It is useless to fetch at the
  // moment it is needed, which is precisely when there is no connection.
  useEffect(() => {
    if (!online) {
      void refreshCapability()
      void refreshCounts()
      return
    }
    let cancelled = false
    void (async () => {
      try {
        await refillPool(todayISO)
      } catch {
        /* leaves the pool short; the capability predicate reports it honestly */
      }
      if (!cancelled) {
        await refreshCapability()
        await refreshCounts()
      }
    })()
    return () => {
      cancelled = true
    }
  }, [online, todayISO, refreshCapability, refreshCounts])

  /**
   * Take a sale offline: claim a reserved number and queue it for replay.
   *
   * Three shapes go through this one path, because everything after the queue
   * write — the counts, the capability refresh — is identical for all of them:
   *
   *   heldOpId      append to an order still queued on THIS device
   *   targetOrderId append to an order that already exists on the server
   *   neither       a brand-new order — the only shape that consumes a number
   */
  const captureOffline = useCallback(
    async (input: CaptureInput): Promise<CaptureResult> => {
      // An append rides on the order it targets, so it must NOT consume a
      // reserved number — that number belongs to a future new order. Its number
      // is supplied by the caller from the order being continued.
      const appending = Boolean(input.heldOpId || input.targetOrderId)
      const orderNumber = appending
        ? (input.existingOrderNumber ?? '')
        : await takeReservedNumber(todayISO)
      if (!orderNumber) {
        await refreshCapability()
        return { ok: false, reason: 'not_ready' }
      }

      const capturedAt = Date.now()
      const lines = input.lines.map((l) => ({
        productId: l.productId,
        quantity: l.quantity,
        ...(l.special_instructions ? { special_instructions: l.special_instructions } : {}),
      }))

      let held: OfflineOrder | null = null
      if (input.heldOpId) {
        // F-FE-N31: only safe while the entry is still pending. If replay posted
        // it while the cashier was composing, say so rather than appending to a
        // queue entry that will never be replayed again — those items would
        // never reach a bill.
        held = await appendLinesToPendingOfflineOrder(input.heldOpId, lines)
        if (!held) {
          await refreshCounts()
          return { ok: false, reason: 'already_synced' }
        }
      } else {
        await enqueueOfflineOrder({
          orderNumber,
          capturedAt,
          orderType: input.orderType,
          userId: input.userId,
          ...(input.targetOrderId ? { targetOrderId: input.targetOrderId } : {}),
          lines,
        })
      }

      await refreshCapability()
      await refreshCounts()
      return { ok: true, orderNumber, held }
    },
    [todayISO, refreshCapability, refreshCounts],
  )

  /** Drain the queue. Safe to call repeatedly — replay is idempotent server-side. */
  const replayNow = useCallback(async (): Promise<ReplaySummary> => {
    const summary = await replayOfflineQueue({
      replay: async (op) => {
        try {
          // F-CTRL-N37: an operation targeting an existing order is an APPEND,
          // not a new order. It goes through the ordinary add-items endpoint —
          // carrying the op id so a retry cannot add the same items twice.
          if (op.target_order_id) {
            const add = await apiClient.addItemsToOrder(
              op.target_order_id,
              op.lines,
              op.op_id,
            )
            if (!add.success) return { errorCode: add.error ?? 'unknown_error' }
            return { errorCode: null }
          }
          const res = await apiClient.replayOfflineOrder(op)
          return { errorCode: res.success ? null : (res.error ?? 'unknown_error') }
        } catch (e) {
          // The API client throws on non-2xx; the stable code is what the
          // classifier keys off, so dig it out rather than flattening to
          // "network error" and retrying a permanent conflict forever.
          const code = (e as { code?: string; error?: string })?.code
            ?? (e as { error?: string })?.error
            ?? 'network_error'
          return { errorCode: code }
        }
      },
      report: async (op, failureCode) => {
        try {
          const res = await apiClient.reportUnreconciledOffline({
            op_id: op.opId,
            order_number: op.orderNumber,
            target_order_id: op.targetOrderId ?? null,
            captured_at: op.capturedAt,
            failure_code: failureCode,
            lines: op.lines,
          })
          return res.success === true
        } catch {
          // Leave it pending — the next drain retries. Never swallow.
          return false
        }
      },
    })
    await refreshCounts()
    await refreshCapability()
    return summary
  }, [refreshCounts, refreshCapability])

  // Drain automatically the moment the connection returns.
  const prevOnline = useRef(online)
  const onReplayRef = useRef<((s: ReplaySummary) => void) | null>(null)
  useEffect(() => {
    const was = prevOnline.current
    prevOnline.current = online
    if (!was && online) {
      void replayNow().then((s) => onReplayRef.current?.(s))
    }
  }, [online, replayNow])

  /** Register a callback for the automatic post-reconnect drain. */
  const onReplayComplete = useCallback((fn: (s: ReplaySummary) => void) => {
    onReplayRef.current = fn
  }, [])

  return {
    capability,
    pendingCount,
    unreconciledCount,
    captureOffline,
    replayNow,
    onReplayComplete,
    refreshCounts,
  }
}
